import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import api from 'src/api'
import { Agent, SmartPatrolOffenseAggregation } from 'src/api/gen'
import { ISmartPatrolOffenseListFilters } from 'src/api/types/smartPatrol'
import { getEnumEntries } from 'src/commons/filters/utils'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { stringToZonedDateTime, ITemporalRange, zonedDateTimeToISOString } from 'src/commons/temporalHelpers'
import { useAgencySettings } from 'src/layout/AgencySettings'
import { useProviders, useUserProfile } from 'src/layout/hooks'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { ISidePanelProps, SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'

export interface ISmartPatrolSidePanel extends ISidePanelProps {
  aggregations: SmartPatrolOffenseAggregation
  defaultFilters: ISmartPatrolOffenseListFilters
  initialFilters: ISmartPatrolOffenseListFilters
}

export const useAgents = function () {
  const [agents, setAgents] = useState<Agent[]>([])

  useEffect(() => {
    const abortController = new AbortController()

    api.smartPatrol
      .smartPatrolAgentsList({ options: { signal: abortController.signal } })
      .then(setAgents)
      .catch((reason) => {
        if (!abortController.signal.aborted) {
          console.error(reason)
        }
      })

    return () => abortController.abort()
  }, [])

  return { agents }
}

export default forwardRef(function SmartPatrolSidePanel(props: ISmartPatrolSidePanel, ref) {
  const { t } = useTranslation()
  const { vehicle_types } = useAgencySettings()
  const { activeProviders } = useProviders()
  const { agents } = useAgents()
  const { jurisdiction } = useUserProfile()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  const handleFiltersChange = (id: string, values: string[]) => {
    setFilters((f) => ({ ...f, [id]: values }))
  }

  const handleTimeFiltersChange = (_id: string, values: ITemporalRange) => {
    setFilters((f) => ({
      ...f,
      created_at_after: values.from ? zonedDateTimeToISOString(values.from) : undefined,
      created_at_before: values.to ? zonedDateTimeToISOString(values.to) : undefined,
    }))
  }

  const vehicleTypeEntries = getEnumEntries(vehicle_types, enumsTranslationKeys.DeviceType)

  function onFromChanged(value?: Temporal.ZonedDateTime) {
    handleTimeFiltersChange('created_at_after', {
      from: value,
      to: filters.created_at_before ? stringToZonedDateTime(filters.created_at_before) : undefined,
    })
  }

  function onToChanged(value?: Temporal.ZonedDateTime) {
    handleTimeFiltersChange('created_at_before', {
      from: filters.created_at_after ? stringToZonedDateTime(filters.created_at_after) : undefined,
      to: value,
    })
  }

  function onBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    handleTimeFiltersChange('created_at_both', { from: valueFrom, to: valueTo })
  }

  return (
    <SidePanel
      headerProps={{
        title: 'Filters',
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <CheckboxListFilter
        filterId={'vehicule_types'}
        items={vehicleTypeEntries}
        values={filters['vehicule_types']}
        title={t('smart_patrol.vehicle_type')}
        onFilterChange={handleFiltersChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'provider_names'}
        items={activeProviders.map((prov) => ({ id: prov.name, label: prov.name }))}
        values={filters.provider_names}
        title={t('smart_patrol.provider')}
        onFilterChange={handleFiltersChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'type'}
        items={jurisdiction.smart_patrol_violation_types.map((violationType) => ({
          id: violationType.id,
          label: violationType.localized_name || violationType.name,
          endAdornment: props.aggregations.type[violationType.id],
        }))}
        values={filters.type}
        title={t('smart_patrol.violation_type')}
        onFilterChange={handleFiltersChange}
        displayCheckbox
      />

      <TimeRangeFilterPro
        filterId={'reporting_datetime'}
        format="L LT"
        values={{
          from: filters.created_at_after ? stringToZonedDateTime(filters.created_at_after) : undefined,
          to: filters.created_at_before ? stringToZonedDateTime(filters.created_at_before) : undefined,
        }}
        title={t('smart_patrol.reporting_datetime')}
        onFromChanged={onFromChanged}
        onToChanged={onToChanged}
        onBothChanged={onBothChanged}
        disableFuture
        normalize
        defaultExpanded
      />

      <CheckboxListFilter
        filterId={'agent_names'}
        items={agents.map((agent) => ({
          id: agent.full_name,
          label: agent.full_name,
          endAdornment: props.aggregations.agent_names[agent.full_name],
        }))}
        values={filters.agent_names}
        title={t('smart_patrol.agent_name')}
        onFilterChange={handleFiltersChange}
        displayCheckbox
      />
    </SidePanel>
  )
})
